import {
  HttpContextToken,
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { environment } from '@env/environment';
import { NotificationService } from '@core/services/notification.service';
import { AccessDeniedService } from '@core/services/access-denied.service';
import { ApiErrorHelper } from '@core/utils/helpers/api-error.helper';
import { API_ERROR_CODES } from '@core/models/generated/api-contract.generated';
import { KIOSK_DEVICE_PATH_SEGMENT } from '@modules/kiosk-device/kiosk-token.interceptor';

/** Opt a write out of the central toast (e.g. background polling that should fail silently). */
export const SILENT_FORBIDDEN = new HttpContextToken<boolean>(() => false);

/**
 * Opt a **read** into the global access-denied overlay: set this on a screen's ONE authoritative record
 * fetch (`getById`/`getForSigner`), so a 403 there shows the shared overlay via {@link AccessDeniedService}
 * instead of the component wiring its own panel. Do NOT set it on the PMS read pages' probe reads — those
 * expect benign 403s to fall through to the next scoped endpoint, and an overlay would deny a working screen.
 */
export const FORBIDDEN_OVERLAY = new HttpContextToken<boolean>(() => false);

const FALLBACK = 'You do not have permission to perform this action.';

// Reads that go out as POST (grid State / bulk fetches). A 403 here is "this section isn't yours",
// not a failed action, so it stays as quiet as a GET.
const READ_ROUTES = ['/query', '/all', '/paged', '/lookup'];

/**
 * Surfaces a 403 from the API as one toast carrying the backend's own message — a hand-written reason
 * where there is one (`ResponseDetail.Forbidden("You can only act on your own task.")`), otherwise the
 * same plain sentence as {@link FALLBACK}: a bare permission-gate rejection deliberately names no
 * permission key (`GlobalExceptionMiddleware` keeps that detail in the server log).
 *
 * Only **writes** toast. PMS procedures/processes/policies deliberately probe several scoped
 * endpoints and swallow the 403s the caller doesn't hold (`procedure-run-page` — which is also the
 * preview/read surface — plus `policy-read-page`, `files-page`, `tasks-page`); toasting per denied
 * read would bury a working screen in "Access denied". Reads stay owned by the component that made them.
 *
 * A component that also reports the error shows the same backend text, so NotificationService
 * de-duplicates it into this one toast instead of stacking a second.
 */
export const forbiddenInterceptor: HttpInterceptorFn = (req, next) => {
  const notification = inject(NotificationService);
  const accessDenied = inject(AccessDeniedService);
  const router = inject(Router);

  return next(req).pipe(
    catchError((error: unknown) => {
      const isForbidden = error instanceof HttpErrorResponse && error.status === 403;

      // Sign-in switched off mid-session: every later request would fail the same way, and a read
      // fails silently, so the person is left with a screen that does nothing and says nothing. Send
      // them to the login page, which already knows how to explain this code.
      if (isForbidden && ApiErrorHelper.hasCode(error, API_ERROR_CODES.accessRestricted)) {
        if (!router.url.startsWith('/login')) {
          void router.navigate(['/login'], { queryParams: { error: 'account_restricted' } });
        }
      } else if (isForbidden && isNotifiable(req)) {
        notification.error(ApiErrorHelper.firstMessage(error, FALLBACK), 'Access denied');
      } else if (isForbidden && req.context.get(FORBIDDEN_OVERLAY)) {
        // An authoritative record read the component opted in: show the one global access-denied overlay
        // carrying the backend's reason, instead of the component rendering its own 403 panel.
        accessDenied.showDenied(ApiErrorHelper.firstMessage(error, FALLBACK));
      }

      return throwError(() => error);
    })
  );
};

function isNotifiable(req: HttpRequest<unknown>): boolean {
  if (req.context.get(SILENT_FORBIDDEN)) return false;
  return isTenantApi(req) && isUserAction(req);
}

function isTenantApi(req: HttpRequest<unknown>): boolean {
  if (!req.url.startsWith(environment.apiBaseUrl)) return false;
  return !req.url.includes('/auth/') && !req.url.includes(KIOSK_DEVICE_PATH_SEGMENT);
}

function isUserAction(req: HttpRequest<unknown>): boolean {
  if (req.method === 'GET') return false;
  const path = trimTrailingSlashes(req.url.split('?')[0]);
  return !READ_ROUTES.some((route) => path.endsWith(route));
}

function trimTrailingSlashes(url: string): string {
  let end = url.length;
  while (end > 0 && url[end - 1] === '/') end--;
  return url.slice(0, end);
}
